import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import { PackItem } from '../RafflePage';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { isDiscountEligibleSubscriptionPackage } from '../../../../../../../utils/subscriptionDiscounts';

type SubscriptionCarouselProps = {
  items: PackItem[];
  selectedId?: string;
  onSelect?: (id: string) => void;
  lastPackDiscountPercent?: number; // e.g. 30 or 50
};

const SubscriptionCarousel = ({
  items,
  selectedId,
  onSelect,
  lastPackDiscountPercent
}: SubscriptionCarouselProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const scrollerRef = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isMobile) return;
    if (!items.length) return;
    if (selectedId) return;

    const el = scrollerRef.current;
    if (!el) return;

    setActiveIndex(0);
    el.scrollLeft = 0;
  }, [isMobile, items.length, selectedId]);

  const VALUE_FOOTER_COPY = [
    { usually: 'A$48.00', save: 'Save A$38.10 (79% off)' },
    { usually: 'A$120.00', save: 'Save A$95.10 (79% off)' },
    { usually: 'A$300.00', save: 'Save A$250.10 (83% off)' },
    { usually: 'A$600.00', save: 'Save A$530.10 (88% off)' }
  ] as const;

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;

    const children = Array.from(el.children) as HTMLElement[];
    if (!children.length) return;

    const center = el.scrollLeft + el.clientWidth / 2;

    let bestIdx = 0;
    let bestDist = Infinity;

    children.forEach((c, i) => {
      const cCenter = c.offsetLeft + c.clientWidth / 2;
      const dist = Math.abs(center - cCenter);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    });

    setActiveIndex(bestIdx);
  };

  const handleSelect = (id: string) => onSelect?.(id);

  const goToMobileIndex = (nextIndex: number) => {
    const el = scrollerRef.current;
    if (!el || !items.length) return;

    const normalizedIndex = (nextIndex + items.length) % items.length;
    const child = el.children[normalizedIndex] as HTMLElement | undefined;

    setActiveIndex(normalizedIndex);

    child?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest'
    });
  };

  const Dots = ({ count, active }: { count: number; active: number }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1.6,
        mt: 1.2
      }}
    >
      {Array.from({ length: count }).map((_, i) => {
        const isActive = i === active;
        return (
          <Box
            key={i}
            sx={{
              width: isActive ? 64 : 14,
              height: 14,
              borderRadius: 999,
              transition:
                'width 0.25s ease, opacity 0.25s ease, filter 0.25s ease',
              background: isActive ? items[i].dotColor : 'gray',
              opacity: isActive ? 1 : 0.7
            }}
          />
        );
      })}
    </Box>
  );

  const money = (n: number | string | undefined) => {
    const x = Number(n ?? 0);
    if (!Number.isFinite(x)) return String(n ?? '');
    return x.toFixed(x % 1 === 0 ? 0 : 2);
  };

  const weeklyMoney = (monthlyPrice: number) =>
    ((monthlyPrice * 12) / 52).toFixed(2);

  const getBonusStartingEntries = (pack: PackItem) =>
    Math.max(0, Math.floor(Number(pack.entries || 0) * 10));

  const Row = ({
    color,
    textColor = '#334155',
    children
  }: {
    color: string;
    textColor?: string;
    children: ReactNode;
  }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3 }}>
      <CheckIcon sx={{ fontSize: 20, color, flexShrink: 0 }} />

      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 700,
          color: textColor,
          lineHeight: 1.35
        }}
      >
        {children}
      </Typography>
    </Box>
  );

  const parseSavePercent = (s?: string) => {
    const m = String(s || '').match(/\((\d+)\%\s*off\)/i);
    return m ? Number(m[1]) : 0;
  };

  const topBadgesById = useMemo(() => {
    const base = [...items];

    const ranked = base
      .map((p, i) => ({
        id: p.id,
        i,
        pct: parseSavePercent(VALUE_FOOTER_COPY[i]?.save),
        entries: Number(p.entries || 0),
        nfts: Number(p.nfts || 0),
        price: Number(p.price || 0)
      }))
      .sort((a, b) => {
        if (b.pct !== a.pct) return b.pct - a.pct;
        if (b.nfts !== a.nfts) return b.nfts - a.nfts;
        if (b.entries !== a.entries) return b.entries - a.entries;
        return a.price - b.price;
      });

    return {
      best: ranked[0]?.id ?? null,
      popular: ranked[1]?.id ?? null
    };
  }, [items]);

  const indexById = useMemo(() => {
    const m = new Map<string, number>();
    items.forEach((p, i) => m.set(p.id, i));
    return m;
  }, [items]);

  const firstHexFromGradient = (s?: string) => {
    const m = String(s || '').match(/#([0-9a-f]{3,8})/i);
    return m ? `#${m[1]}` : '#7CFFFA';
  };

  const TopRibbon = ({
    text,
    gradient
  }: {
    text: string;
    gradient: string;
  }) => {
    const accent = firstHexFromGradient(gradient);

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 44,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: 'hidden',
          zIndex: 3,
          background: gradient,
          borderBottom: `1px solid ${accent}55`,
          filter: `drop-shadow(0 10px 18px ${accent}33)`
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(120deg, rgba(255,255,255,0.18), rgba(255,255,255,0) 55%)',
            opacity: 0.55
          }}
        />

        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 950,
              fontSize: 13,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              textShadow: `0 0 16px ${accent}AA`
            }}
          >
            {text}
          </Typography>
        </Box>
      </Box>
    );
  };

  const SubscriptionCard = ({
    pack,
    selected,
    onSelect,
    index
  }: {
    pack: PackItem;
    selected: boolean;
    onSelect: () => void;
    index: number;
  }) => {
    const valueCopy = VALUE_FOOTER_COPY[index];

    const isBest = pack.id === topBadgesById.best;
    const isPopular = pack.id === topBadgesById.popular;
    const isFeatured = isBest || isPopular;

    const gradient = pack.dotColor;
    const accent = firstHexFromGradient(gradient);

    const hasDiscount =
      typeof lastPackDiscountPercent === 'number' &&
      lastPackDiscountPercent > 0 &&
      isDiscountEligibleSubscriptionPackage(pack.id);

    const originalPrice = Number(pack.price || 0);
    const discountedPrice = hasDiscount
      ? originalPrice * (1 - lastPackDiscountPercent / 100)
      : originalPrice;

    return (
      <Box
        onClick={onSelect}
        role="button"
        tabIndex={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          p: 3,
          pt: isFeatured ? 6.2 : 3,
          cursor: 'pointer',
          userSelect: 'none',
          background: `linear-gradient(180deg, ${accent}10 0%, #ffffff 24%, #ffffff 100%)`,
          border: selected
            ? `2px solid ${accent}`
            : isFeatured
              ? `1px solid ${accent}66`
              : '1px solid rgba(18,31,38,0.10)',
          boxShadow: selected
            ? `0 20px 48px ${accent}24`
            : `0 16px 38px ${accent}14`,
          transition:
            'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 22px 52px ${accent}22`
          }
        }}
      >
        {isBest && <TopRibbon text="Best Value" gradient={gradient} />}
        {isPopular && <TopRibbon text="Most Popular" gradient={gradient} />}

        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 900,
            color: '#111827',
            textAlign: 'center'
          }}
        >
          {pack.title}
        </Typography>

        <Box sx={{ mt: 1.2, textAlign: 'center' }}>
          {hasDiscount && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 0.85,
                  borderRadius: 999,
                  background: `linear-gradient(135deg, ${accent}ee 0%, ${accent}55 100%)`,
                  boxShadow: `0 12px 28px ${accent}22`
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 1000,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: '#081012',
                    lineHeight: 1
                  }}
                >
                  {lastPackDiscountPercent}% OFF FIRST MONTH
                </Typography>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mt: hasDiscount ? 1.1 : 0,
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: 0.6,
              whiteSpace: 'nowrap'
            }}
          >
            <Typography
              sx={{
                fontSize: 44,
                fontWeight: 950,
                color: hasDiscount ? '#d4a500' : '#0f172a'
              }}
            >
              A${weeklyMoney(discountedPrice)}
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 900,
                color: '#4b5563'
              }}
            >
              /week*
            </Typography>
            {hasDiscount && (
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: 'rgba(17,24,39,0.38)',
                  textDecoration: 'line-through',
                  textDecorationThickness: '2px'
                }}
              >
                A${weeklyMoney(originalPrice)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          {/* Bonus banner above the per-draw entries line, matching
              SubscriptionUpgradeCards. The two components show the same
              commercial content in different layouts, and the CODEMAP flags
              divergence between them as a mispricing risk — so they are ordered
              the same on purpose. This one is rendered by RafflePaymentPage and
              EmailPromo.

              The margins moved with the blocks: the wrapper above already
              supplies the gap from the price, so the banner leads with `mt: 0`
              and the entries line picks up the `mt: 2` the banner used to carry.
              Leaving them attached to their original blocks would have doubled
              the top gap and butted the entries line against the banner. */}
          <Box
            sx={{
              mt: 0,
              borderRadius: 2.5,
              border: '1px solid rgba(16,185,129,0.32)',
              background:
                'linear-gradient(135deg, rgba(209,250,229,0.98) 0%, rgba(236,253,245,0.98) 100%)',
              px: 2,
              py: 1.45,
              boxShadow: '0 12px 26px rgba(5,150,105,0.10)'
            }}
          >
            <Typography
              sx={{
                color: '#047857',
                fontSize: { xs: 15, md: 16 },
                fontWeight: 950,
                lineHeight: 1.3,
                textAlign: 'center',
                textTransform: 'uppercase'
              }}
            >
              Bonus offer: start with{' '}
              {getBonusStartingEntries(pack).toLocaleString('en-AU')} entries
              from the get go!
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: 2,
              color: accent,
              fontSize: { xs: 25, md: 28 },
              fontWeight: 950,
              lineHeight: 1.22,
              py: 1.6,
              borderTop: '1px solid rgba(15,23,42,0.14)',
              borderBottom: '1px solid rgba(15,23,42,0.14)'
            }}
          >
            Get {pack.entries} stacking giveaway entries into every draw
          </Typography>

          <Box sx={{ mt: 2.2, display: 'grid', gap: 1.4 }}>
            <Row color={accent}>
              <b>{pack.nfts}</b> Digital Collectible rewards per month
            </Row>
            <Row color={accent}>
              <b>A${money(pack.tokens)}</b> worth of INC tokens per month
            </Row>
            {/* Gold on every plan, by request (2026-10-05) — the same gold as
                SubscriptionUpgradeCards' "Cancel anytime" pill, not the pack's accent. */}
            <Row color="#d08a00" textColor="#92400e">
              Cancel anytime
            </Row>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            sx={{
              height: 44,
              borderRadius: 2,
              fontWeight: 900,
              background: selected ? `${accent}14` : '#f8fafc',
              color: selected ? accent : '#111827',
              border: selected
                ? `1px solid ${accent}`
                : '1px solid rgba(18,31,38,0.10)',
              boxShadow: 'none',
              '&:hover': {
                background: selected ? `${accent}1a` : '#eef2f7'
              }
            }}
          >
            {selected ? 'Selected' : 'Select'}
          </Button>

          <Typography
            sx={{
              mt: 0.8,
              px: 0.5,
              textAlign: 'center',
              fontWeight: 700,
              fontSize: { xs: 10, md: 11 },
              lineHeight: 1.3,
              color: '#64748b'
            }}
          >
            {hasDiscount
              ? `*First-month equivalent. A$${money(
                  discountedPrice
                )} charged today, then A$${money(originalPrice)} monthly.`
              : `*Equivalent weekly price. Charged A$${money(
                  originalPrice
                )} monthly.`}
          </Typography>
        </Box>

        <Box
          sx={{ mt: 2.2, pt: 2, borderTop: '1px solid rgba(18,31,38,0.10)' }}
        >
          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 750,
              fontSize: 13,
              color: 'rgba(17,24,39,0.40)'
            }}
          >
            Usually valued at
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              mt: 0.6,
              fontWeight: 800,
              fontSize: 18,
              color: 'rgba(17,24,39,0.35)',
              textDecoration: 'line-through',
              textDecorationThickness: '2px',
              textDecorationColor: 'rgba(17,24,39,0.35)'
            }}
          >
            {valueCopy?.usually}
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              mt: 1,
              fontWeight: 950,
              fontSize: 16,
              color: accent
            }}
          >
            {valueCopy?.save}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', minWidth: 0 }}
    >
      {isMobile ? (
        <Box
          sx={{
            mt: 2,
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            minWidth: 0,
            position: 'relative'
          }}
        >
          <Box sx={{ overflow: 'hidden', width: '100%', maxWidth: '100%' }}>
            <Box
              ref={scrollerRef}
              onScroll={onScroll}
              sx={{
                display: 'flex',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                px: 2,
                pb: 3,
                scrollPadding: '0 16px',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none'
              }}
            >
              {items.map((pack, idx) => (
                <Box
                  key={pack.id}
                  onClick={(e) => {
                    if (!isMobile) return;

                    (e.currentTarget as HTMLElement).scrollIntoView({
                      behavior: 'smooth',
                      inline: 'center',
                      block: 'nearest'
                    });

                    handleSelect(pack.id);
                  }}
                  sx={{
                    flex: { xs: '0 0 95%', sm: '0 0 78%' },
                    scrollSnapAlign: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Box sx={{ px: 1.5, width: '100%' }}>
                    <SubscriptionCard
                      pack={pack}
                      index={indexById.get(pack.id) ?? idx}
                      selected={pack.id === selectedId}
                      onSelect={() => handleSelect(pack.id)}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {items.length > 1 && (
            <>
              <IconButton
                onClick={() => goToMobileIndex(activeIndex - 1)}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: { xs: -4, sm: -44 },
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  width: { xs: 38, sm: 46 },
                  height: { xs: 38, sm: 46 },
                  background: 'rgba(15,23,42,0.72)',
                  color: '#ffffff',
                  border: '1px solid rgba(255,255,255,0.28)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': { background: 'rgba(15,23,42,0.9)' }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <IconButton
                onClick={() => goToMobileIndex(activeIndex + 1)}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: { xs: -4, sm: -44 },
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  width: { xs: 38, sm: 46 },
                  height: { xs: 38, sm: 46 },
                  background: 'rgba(15,23,42,0.72)',
                  color: '#ffffff',
                  border: '1px solid rgba(255,255,255,0.28)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': { background: 'rgba(15,23,42,0.9)' }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          <Dots count={items.length} active={activeIndex} />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: 3
          }}
        >
          {[...items].map((p, idx) => (
            <SubscriptionCard
              key={p.id}
              pack={p}
              index={indexById.get(p.id) ?? idx}
              selected={p.id === selectedId}
              onSelect={() => handleSelect(p.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionCarousel;
